/**
 * Задание stylepack/option-indicator (слово владельца 21.10.2026: повторять
 * чужую воронку в точности — у Calm отметка варианта справа, скруглённый
 * квадрат 20 px с бледной рамкой). Место и вид отметки выбора у
 * single_choice и multiple_choice.
 *
 * ЗЕРКАЛО канона сервера `backend/src/ai-funnels/block-schema/option-indicator.ts`:
 * перечни и границы ниже читает с диска сторож `option-indicator.ends.spec.ts`
 * и сверяет со схемой, типами, панелью и словарём в обе стороны — форму
 * `[...] as const` и `{ min, max } as const` не менять, не обновив его. Он же
 * требует, чтобы каждое значение, кроме значения по умолчанию, было названо
 * здесь сравнением (`position === '…'`, `style === '…'`).
 *
 * Отдельный модуль, а не optionGrid.ts / OptionRowContent.tsx: рядом идёт
 * ветка с описанием варианта, которая правит те файлы.
 *
 * Как рисуется. Родной `<input>` остаётся в строке всегда — по нему кликают
 * (строка — это `<label>`), его видят клавиатура и экранный диктор. При виде
 * «box» он спрятан тем же способом, что у «без отметки», а рядом рисуется
 * квадрат или круг (OptionIndicatorBox.tsx). Место — порядком в строке-flex:
 * справа отметка встаёт последней, правее общего значка справа.
 * `optionIndicator: 'none'` главнее всего — отметки нет совсем.
 *
 * Нет полей — пустые накладки: разметка старой воронки та же байт в байт.
 */
import type React from 'react';
import { resolveOptionAccent } from './optionAccentColor';
import { optionTrailingIconOf, type OptionIconFields } from './optionGrid';
import {
  OPTION_INDICATOR_HIDDEN_STYLE,
  optionIndicatorAlignOverride,
  optionIndicatorHidden,
  type OptionRowFields,
} from './optionRowStyle';

export const OPTION_INDICATOR_BLOCK_TYPES = [
  'single_choice',
  'multiple_choice',
] as const;
export const OPTION_INDICATOR_POSITIONS = ['left', 'right'] as const;
export const OPTION_INDICATOR_STYLES = ['native', 'box'] as const;
export const OPTION_INDICATOR_SIZE_BOUNDS = { min: 12, max: 32 } as const;
export const OPTION_INDICATOR_RADIUS_BOUNDS = { min: 0, max: 16 } as const;

export type OptionIndicatorPosition =
  (typeof OPTION_INDICATOR_POSITIONS)[number];
export type OptionIndicatorStyle = (typeof OPTION_INDICATOR_STYLES)[number];
/** Одиночный выбор (кружок по умолчанию) или множественный (квадрат 4 px). */
export type OptionIndicatorKind = 'single' | 'multiple';

/** Размер нарисованной отметки, когда автор его не задал, px. */
export const OPTION_INDICATOR_DEFAULT_SIZE = 20;
/** Скругление квадрата множественного выбора по умолчанию, px. */
export const OPTION_INDICATOR_DEFAULT_MULTI_RADIUS = 4;
/** Бледная рамка пустой отметки, когда цвет не задан. */
const DEFAULT_BORDER = 'var(--color-border-primary, #d9d9d9)';
/** Галочка внутри отмеченного — белая (решено в задании). */
export const OPTION_INDICATOR_TICK_COLOR = '#fff';

/** Поля блока, которые читает отметка. */
export interface OptionIndicatorFields
  extends
    Pick<OptionIconFields, 'optionTrailingIcon'>,
    Pick<OptionRowFields, 'optionIndicatorAlign'> {
  optionIndicator?: 'auto' | 'none';
  optionIndicatorPosition?: OptionIndicatorPosition;
  optionIndicatorStyle?: OptionIndicatorStyle;
  optionIndicatorSize?: number;
  optionIndicatorRadius?: number;
  optionIndicatorBorderColor?: string;
  optionIndicatorCheckedColor?: string;
  optionAccentColor?: string;
}

const isOneOf = <T extends string>(list: readonly T[], v: unknown): v is T =>
  (list as readonly unknown[]).includes(v);

export function optionIndicatorPositionOf(
  fields: OptionIndicatorFields,
): OptionIndicatorPosition {
  const v = fields.optionIndicatorPosition;
  return isOneOf(OPTION_INDICATOR_POSITIONS, v) ? v : 'left';
}

export function optionIndicatorStyleOf(
  fields: OptionIndicatorFields,
): OptionIndicatorStyle {
  const v = fields.optionIndicatorStyle;
  return isOneOf(OPTION_INDICATOR_STYLES, v) ? v : 'native';
}

function clampInt(v: number, b: { min: number; max: number }): number {
  return Math.round(Math.min(b.max, Math.max(b.min, v)));
}

/** Размер в границах; нет поля — 20 px. */
export function optionIndicatorSizeOf(fields: OptionIndicatorFields): number {
  const v = fields.optionIndicatorSize;
  return typeof v === 'number' && Number.isFinite(v)
    ? clampInt(v, OPTION_INDICATOR_SIZE_BOUNDS)
    : OPTION_INDICATOR_DEFAULT_SIZE;
}

/** Своё скругление в границах; нет поля — круг у одиночного, 4 у множественного. */
export function optionIndicatorRadiusOf(
  fields: OptionIndicatorFields,
  kind: OptionIndicatorKind,
): number | '50%' {
  const v = fields.optionIndicatorRadius;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return clampInt(v, OPTION_INDICATOR_RADIUS_BOUNDS);
  }
  return kind === 'single' ? '50%' : OPTION_INDICATOR_DEFAULT_MULTI_RADIUS;
}

/** Строгий код цвета или undefined — в inline-стиль уходит только он. */
function strictHex(value: string | undefined): string | undefined {
  const raw = value?.trim();
  return raw && /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(raw)
    ? raw
    : undefined;
}

/** Рисуем свою отметку: вид «box» и отметка не снята «без отметки». */
export function optionIndicatorBoxed(fields: OptionIndicatorFields): boolean {
  const style = optionIndicatorStyleOf(fields);
  return !optionIndicatorHidden(fields) && style === 'box';
}

/**
 * Место видимой отметки в строке-flex. Справа — последней (`order: 1`, у
 * общего значка справа порядок 0) и к правому краю `margin-left: auto`; если
 * общий значок справа есть, к краю его уже отжал свой `auto`-отступ, второй
 * разделил бы свободное место пополам и вытолкнул значок в середину.
 */
function slotStyle(fields: OptionIndicatorFields): React.CSSProperties {
  const position = optionIndicatorPositionOf(fields);
  if (position === 'right') {
    return optionTrailingIconOf(fields) === 'none'
      ? { order: 1, marginLeft: 'auto' }
      : { order: 1 };
  }
  return {};
}

/**
 * Накладка на родной `<input>` (спредится ПОСЛЕ прежнего стиля): «без
 * отметки» и вид «box» прячут его, справа — переносят в конец строки.
 */
export function optionIndicatorInputOverride(
  fields: OptionIndicatorFields,
): React.CSSProperties {
  if (optionIndicatorHidden(fields) || optionIndicatorBoxed(fields)) {
    return OPTION_INDICATOR_HIDDEN_STYLE;
  }
  return slotStyle(fields);
}

/** Строке со спрятанным полем при виде «box» — опора для `position: absolute`. */
export function optionIndicatorRowOverride(fields: OptionIndicatorFields): {
  position?: 'relative';
} {
  return optionIndicatorBoxed(fields) ? { position: 'relative' } : {};
}

/** Стиль нарисованной отметки: пустой — бледная рамка, отмеченный — заливка. */
export function optionIndicatorBoxStyle(
  fields: OptionIndicatorFields,
  kind: OptionIndicatorKind,
  checked: boolean,
): React.CSSProperties {
  const size = optionIndicatorSizeOf(fields);
  const fill =
    strictHex(fields.optionIndicatorCheckedColor) ??
    resolveOptionAccent(strictHex(fields.optionAccentColor));
  const border = strictHex(fields.optionIndicatorBorderColor) ?? DEFAULT_BORDER;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    boxSizing: 'border-box',
    width: size,
    height: size,
    borderRadius: optionIndicatorRadiusOf(fields, kind),
    border: `2px solid ${checked ? fill : border}`,
    background: checked ? fill : 'transparent',
    transition:
      'background-color var(--transition-ms, 0ms) ease, border-color var(--transition-ms, 0ms) ease',
    order: -1,
    ...slotStyle(fields),
    // Шов сведения option-indicator × option-indicator-align: при виде «box»
    // родное поле спрятано, и положение «сверху» держит сама нарисованная
    // отметка — та же накладка, что у родного поля (optionRowStyle.ts).
    ...optionIndicatorAlignOverride(fields),
  };
}
